import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useCurriculumContent } from 'layers/content/v3/hooks/useCurriculumContent'
import LoadingParent from 'components/LoadingParent'
import { appsToRemoveProductSegment } from 'layers/content/v2/Hocs/RouteContentGetter'
import { stripProductAndProgramSegments } from 'utils/routing/routeHelpers'
import { transformCurriculumResponse } from 'layers/content/v3/transforms/transformCurriculumResponse'
import { RouteContentProductAppMap } from 'utils/routing/routeProductMaps'

/**
 * HOC that fetches content from the curriculum service (POST /v1/content).
 * Drop-in replacement for RouteContentGetter behind the feature flag.
 *
 * Curriculum-service integration lives under `layers/content/v3`; this HOC
 * still uses v2 `RouteContentGetter` exports where the legacy pipeline shares
 * helpers (e.g. path segment stripping).
 *
 * The curriculum service combines route resolution + content fetching into
 * a single call, replacing both useRouteGetter and useContentGetter.
 */
export function CurriculumRouteContentGetter(Component, options) {
  function normalizeRoute(route = '') {
    // Canonicalize pathway base routes so curriculum matching resolves
    // the same node as the legacy route-service flow.
    const pathwayBase = /^\/product\/(high-school|middle-school|elementary)\/pathways\/[^/]+$/
    if (pathwayBase.test(route)) {
      return `${route}/overview`
    }

    return route
  }

  function normalizeDigitalWellBeingRoute(route = '') {
    if (route.startsWith('/product/digital-well-being/')) {
      return route.replace('/product/digital-well-being', '')
    }

    if (route.startsWith('/digital-well-being/')) {
      return route.replace('/digital-well-being', '')
    }

    return route
  }

  function resolveSiteId(currentSite) {
    if (currentSite?.siteId != null) {
      return currentSite.siteId
    }

    if (typeof window === 'undefined') {
      return null
    }

    const search = window?.location?.search || ''
    const query = new URLSearchParams(search)
    const siteFromQuery = query.get('siteId') || query.get('site')

    if (!siteFromQuery) {
      return null
    }

    const parsedSiteId = Number(siteFromQuery)
    return Number.isNaN(parsedSiteId) ? null : parsedSiteId
  }

  function defaultRouteResolver(match) {
    if (match?.url) return match.url
    if (typeof window !== 'undefined') return window?.location?.pathname || ''
    return ''
  }

  function CurriculumRouteContentGetterComponent(props) {
    const { match, locale: initialLocale, currentSite } = props
    const { routeResolver = defaultRouteResolver, app } = options
    const [locale, setLocale] = useState(initialLocale || 'en-US')

    function changeLocale(newLocale) {
      setLocale(newLocale)
    }

    useEffect(() => {
      if (initialLocale && initialLocale !== locale) {
        changeLocale(initialLocale)
      }
    }, [initialLocale])

    const route = normalizeRoute(routeResolver(match))
    const resolvedApp = typeof app === 'function' ? app({ route }) : app
    let resolvedRoute =
      resolvedApp === RouteContentProductAppMap.learnHsProduct
        ? route.replace('/product/', '/')
        : stripProductAndProgramSegments(
            route,
            resolvedApp,
            appsToRemoveProductSegment,
          )

    // DWB is routed as its own product URL, but curriculum service expects it
    // under the K5/MSP lessons trees (e.g. /lessons/teach/...).
    resolvedRoute = normalizeDigitalWellBeingRoute(resolvedRoute)

    if (
      resolvedApp === RouteContentProductAppMap.learnHsProduct &&
      resolvedRoute.startsWith('/learning-experiences/')
    ) {
      resolvedRoute = `/high-school${resolvedRoute}`
    }

    const siteId = resolveSiteId(currentSite)

    const {
      route: routeResult,
      page: pageResult,
      coursePointer,
      error,
      loading,
      contentGroupCode,
    } = useCurriculumContent({
      app: resolvedApp,
      routePath: resolvedRoute,
      siteId,
      locale,
      lazy: !siteId,
    })

    const routeContent = transformCurriculumResponse({
      curriculumRoute: routeResult,
      pageResult,
      app: resolvedApp,
      loading,
      error,
      changeLocale,
      locale,
      coursePointer,
      contentGroupCode,
    })

    if (!!routeContent?.loading && !error) {
      return <LoadingParent loadingText={'Loading...'} />
    }

    return <Component {...props} routeContent={routeContent} />
  }

  CurriculumRouteContentGetterComponent.propTypes = {
    currentSite: PropTypes.shape({
      siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    locale: PropTypes.string,
    match: PropTypes.shape({
      url: PropTypes.string,
    }),
  }

  return CurriculumRouteContentGetterComponent
}
